import { format } from 'date-fns';
import {
  ArrowRight,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileDown,
  Loader2,
  Lock,
  MoreHorizontal,
  Package,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import React from 'react';
import { Manifest, ManifestStatus } from '../../services/manifestService';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

export interface ManifestListProps {
  manifests: Manifest[];
  loading: boolean;
  total: number;
  page: number;
  filters: {
    status: string;
    manifestNo: string;
    search: string;
  };
  onFiltersChange: (filters: {
    status: string;
    manifestNo: string;
    search: string;
  }) => void;
  onPageChange: (page: number) => void;
  onView: (manifest: Manifest) => void;
  onDelete: (manifest: Manifest) => void;
  onLock: (manifest: Manifest) => void;
  onDownload: (manifest: Manifest) => void;
}

const statusConfig: Record<ManifestStatus, { className: string; label: string }> = {
  DRAFT: {
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    label: 'Draft',
  },
  LOCKED: {
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    label: 'Locked',
  },
  IN_TRANSIT: {
    className: 'bg-purple-100 text-purple-800 border-purple-200',
    label: 'In Transit',
  },
  DELIVERED: {
    className: 'bg-green-100 text-green-800 border-green-200',
    label: 'Delivered',
  },
};

const ManifestList: React.FC<ManifestListProps> = ({
  manifests,
  loading,
  total,
  page,
  filters,
  onFiltersChange,
  onPageChange,
  onView,
  onDelete,
  onLock,
  onDownload,
}) => {
  const getStatusBadge = (status: ManifestStatus) => {
    const config = statusConfig[status] || {
      className: 'bg-gray-100 text-gray-800',
      label: status,
    };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const totalPages = Math.ceil(total / 10);
  const formatManifestDate = (value?: string) =>
    value ? format(new Date(value), 'dd/MM/yyyy') : '-';
  const getHubLabel = (hub?: { code?: string; name?: string }) =>
    hub?.code || hub?.name || '-';
  const handleClearFilters = () =>
    onFiltersChange({ status: '', manifestNo: '', search: '' });

  const normalizedSearch = filters.search?.trim() || '';
  const normalizedManifestNo = filters.manifestNo?.trim() || '';
  const statusLabel =
    filters.status && statusConfig[filters.status as ManifestStatus]
      ? statusConfig[filters.status as ManifestStatus].label
      : filters.status;

  const activeFilterChips = [
    ...(normalizedSearch
      ? [
          {
            id: 'search',
            label: 'Search',
            value: normalizedSearch,
            onClear: () => onFiltersChange({ ...filters, search: '' }),
          },
        ]
      : []),
    ...(normalizedManifestNo
      ? [
          {
            id: 'manifest',
            label: 'Manifest No',
            value: normalizedManifestNo,
            onClear: () => onFiltersChange({ ...filters, manifestNo: '' }),
          },
        ]
      : []),
    ...(filters.status
      ? [
          {
            id: 'status',
            label: 'Status',
            value: statusLabel,
            onClear: () => onFiltersChange({ ...filters, status: '' }),
          },
        ]
      : []),
  ];

  const hasActiveFilters = activeFilterChips.length > 0;

  return (
    <div className='space-y-4'>
      {/* Filters */}
      <div className='space-y-3'>
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-center'>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search manifests, flight, run number...'
              value={filters.search}
              onChange={e =>
                onFiltersChange({ ...filters, search: e.target.value })
              }
              onKeyDown={event => {
                if (event.key === 'Escape' && filters.search) {
                  event.preventDefault();
                  onFiltersChange({ ...filters, search: '' });
                }
              }}
              className='w-full pl-9 pr-9'
            />
            {normalizedSearch && (
              <button
                type='button'
                onClick={() => onFiltersChange({ ...filters, search: '' })}
                className='absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground'
                aria-label='Clear search'
              >
                <X className='h-4 w-4' />
              </button>
            )}
          </div>
          <div className='relative'>
            <Input
              placeholder='Filter by Manifest No...'
              value={filters.manifestNo}
              onChange={e =>
                onFiltersChange({ ...filters, manifestNo: e.target.value })
              }
              onKeyDown={event => {
                if (event.key === 'Escape' && filters.manifestNo) {
                  event.preventDefault();
                  onFiltersChange({ ...filters, manifestNo: '' });
                }
              }}
              className='w-full pr-9'
            />
            {normalizedManifestNo && (
              <button
                type='button'
                onClick={() => onFiltersChange({ ...filters, manifestNo: '' })}
                className='absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground'
                aria-label='Clear manifest number filter'
              >
                <X className='h-4 w-4' />
              </button>
            )}
          </div>
          <div className='flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end'>
            <Select
              value={filters.status || 'all'}
              onValueChange={value =>
                onFiltersChange({
                  ...filters,
                  status: value === 'all' ? '' : value,
                })
              }
            >
              <SelectTrigger className='w-full sm:w-40'>
                <SelectValue placeholder='Status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Status</SelectItem>
                <SelectItem value='DRAFT'>Draft</SelectItem>
                <SelectItem value='LOCKED'>Locked</SelectItem>
                <SelectItem value='IN_TRANSIT'>In Transit</SelectItem>
                <SelectItem value='DELIVERED'>Delivered</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant='ghost' size='sm' onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </div>
        {hasActiveFilters && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
              Active filters
            </span>
            {activeFilterChips.map(chip => (
              <Badge
                key={chip.id}
                variant='secondary'
                className='gap-1 pr-1 text-xs font-medium'
              >
                <span className='max-w-[220px] truncate' title={`${chip.label}: ${chip.value}`}>
                  {chip.label}: {chip.value}
                </span>
                <button
                  type='button'
                  onClick={chip.onClear}
                  className='ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/70'
                  aria-label={`Clear ${chip.label}`}
                >
                  <X className='h-3 w-3' />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          <span className='ml-2 text-muted-foreground'>
            Loading manifests...
          </span>
        </div>
      ) : manifests.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
          <Package className='h-12 w-12 mb-4' />
          <p className='text-lg font-medium'>No manifests found</p>
          <p className='text-sm'>
            {hasActiveFilters
              ? 'Try adjusting or clearing filters.'
              : 'Create a new manifest to get started.'}
          </p>
          {hasActiveFilters && (
            <Button
              variant='outline'
              size='sm'
              className='mt-3'
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className='hidden md:block'>
            <div className='border rounded-lg overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-muted/50'>
                    <TableHead>Manifest No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Origin Hub</TableHead>
                    <TableHead>Destination Hub</TableHead>
                    <TableHead>AWBs</TableHead>
                    <TableHead>Weight (kg)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manifests.map(manifest => (
                    <TableRow
                      key={manifest._id}
                      className='hover:bg-muted/30 cursor-pointer'
                      onClick={() => onView(manifest)}
                    >
                      <TableCell className='font-medium'>
                        {manifest.manifestNo}
                      </TableCell>
                      <TableCell>{formatManifestDate(manifest.date)}</TableCell>
                      <TableCell>
                        <Badge variant='outline'>{manifest.manifestType}</Badge>
                      </TableCell>
                      <TableCell>{manifest.vendor?.name || '-'}</TableCell>
                      <TableCell>{getHubLabel(manifest.originHub)}</TableCell>
                      <TableCell>{getHubLabel(manifest.destinationHub)}</TableCell>
                      <TableCell>{manifest.noOfAwb}</TableCell>
                      <TableCell>
                        {manifest.totalChargeableWeight?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell>{getStatusBadge(manifest.status)}</TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className='flex items-center gap-1'>
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => onView(manifest)}
                            title='View'
                          >
                            <Eye className='h-4 w-4' />
                          </Button>
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => onDownload(manifest)}
                            title='Download Report'
                          >
                            <FileDown className='h-4 w-4' />
                          </Button>
                          {manifest.status === 'DRAFT' && (
                            <>
                              <Button
                                variant='ghost'
                                size='icon'
                                onClick={() => onLock(manifest)}
                                title='Lock'
                                className='text-blue-600'
                              >
                                <Lock className='h-4 w-4' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='icon'
                                onClick={() => onDelete(manifest)}
                                title='Delete'
                                className='text-red-600'
                              >
                                <Trash2 className='h-4 w-4' />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className='space-y-3 md:hidden'>
            {manifests.map(manifest => (
              <Card key={manifest._id} className='overflow-hidden'>
                <CardContent className='p-4 space-y-3'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='space-y-1'>
                      <button
                        type='button'
                        className='flex items-center gap-2 text-left font-semibold text-primary hover:underline'
                        onClick={() => onView(manifest)}
                      >
                        <Package className='h-4 w-4' />
                        <span>{manifest.manifestNo}</span>
                      </button>
                      <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                        <Calendar className='h-3 w-3' />
                        <span>{formatManifestDate(manifest.date)}</span>
                      </div>
                    </div>
                    {getStatusBadge(manifest.status)}
                  </div>

                  <div className='grid grid-cols-2 gap-3 text-sm'>
                    <div className='space-y-1'>
                      <p className='text-xs text-muted-foreground'>Vendor</p>
                      <p className='font-medium truncate'>
                        {manifest.vendor?.name || '-'}
                      </p>
                    </div>
                    <div className='space-y-1'>
                      <p className='text-xs text-muted-foreground'>Type</p>
                      <Badge variant='outline'>{manifest.manifestType}</Badge>
                    </div>
                    <div className='space-y-1'>
                      <p className='text-xs text-muted-foreground'>Origin Hub</p>
                      <p className='font-medium truncate'>
                        {getHubLabel(manifest.originHub)}
                      </p>
                    </div>
                    <div className='space-y-1'>
                      <p className='text-xs text-muted-foreground'>
                        Destination Hub
                      </p>
                      <p className='font-medium truncate'>
                        {getHubLabel(manifest.destinationHub)}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-center justify-between border-t pt-3 text-sm'>
                    <div className='flex items-center gap-2 text-muted-foreground'>
                      <span className='font-medium text-foreground'>
                        {manifest.noOfAwb}
                      </span>
                      <span>AWBs</span>
                      <ArrowRight className='h-3 w-3' />
                      <span>
                        {manifest.totalChargeableWeight?.toFixed(2) || '0.00'} kg
                      </span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='outline' size='sm'>
                          <MoreHorizontal className='h-4 w-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => onView(manifest)}>
                          <Eye className='mr-2 h-4 w-4' /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onDownload(manifest)}>
                          <FileDown className='mr-2 h-4 w-4' /> Download
                        </DropdownMenuItem>
                        {manifest.status === 'DRAFT' && (
                          <>
                            <DropdownMenuItem onClick={() => onLock(manifest)}>
                              <Lock className='mr-2 h-4 w-4 text-blue-600' />{' '}
                              Lock
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className='text-red-600'
                              onClick={() => onDelete(manifest)}
                            >
                              <Trash2 className='mr-2 h-4 w-4' /> Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <p className='text-xs sm:text-sm text-muted-foreground text-center sm:text-left'>
                Showing {(page - 1) * 10 + 1} to {Math.min(page * 10, total)} of{' '}
                {total} manifests
              </p>
              <div className='flex items-center justify-center gap-2'>
                <Button
                  variant='outline'
                  size='icon'
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className='h-4 w-4' />
                </Button>
                <span className='text-xs sm:text-sm'>
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant='outline'
                  size='icon'
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className='h-4 w-4' />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManifestList;
